"use client";

import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Flex } from "../Flex";
import { IconButton } from "../IconButton";
import { Tooltip } from "../Tooltip";
import { Modal } from "../Modal";
import { Body } from "../Body";
import { Button } from "../Button";
import { Icon } from "../Icon";
import type { ContextAction } from "../../types";
import { useContextActions } from "../../hooks/useContextActions";

export type ContextActionsProps = {
  /**
   * The list of context actions to display
   */
  actions: ContextAction[];
  /**
   * The title to display in the confirmation modal
   */
  title?: ReactNode;
  /**
   * Callback when an action is triggered
   */
  onAction?: (action: ContextAction) => void;
  /**
   * Whether the context actions are disabled
   */
  disabled?: boolean;
};

/**
 * ContextActions component displays a row of inline icon buttons for context actions.
 * Includes delete confirmation modal for delete-type actions.
 */
export const ContextActions: FC<ContextActionsProps> = ({
  actions,
  title,
  disabled,
  onAction,
}) => {
  const { t } = useTranslation();
  const {
    handleActionClick,
    isConfirmationActive,
    setIsConfirmationActive,
    handleDeleteConfirm,
    confirmationTitle,
  } = useContextActions({ actions, title, onAction });

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <>
      <Flex direction="row" gap="xxs" align="center" justify="flex-end">
        {actions.map((action, i) => (
          <Tooltip key={i} value={action.label}>
            <IconButton
              border="default"
              variant={action.type === "delete" ? "opaqueError" : "opaque"}
              icon={action.type === "delete" ? "delete" : "edit"}
              size="xs"
              onClick={() => handleActionClick(action)}
              disabled={disabled}
            />
          </Tooltip>
        ))}
      </Flex>
      {isConfirmationActive && (
        <Modal placement="center">
          <Body>
            {confirmationTitle
              ? t("contextActions.deleteConfirmation", {
                  title: confirmationTitle,
                })
              : t("contextActions.deleteConfirmationDefault")}
          </Body>
          <Flex justify="space-between" align="flex-end" gap="s">
            <Button
              rel="noopener noreferrer"
              variant="background"
              border="default"
              onClick={() => setIsConfirmationActive(false)}
            >
              {t("contextActions.cancel")}
            </Button>
            <Button
              rel="noopener noreferrer"
              variant="error"
              trailing={<Icon icon="delete" size="s2" />}
              onClick={handleDeleteConfirm}
            >
              {t("contextActions.delete")}
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  );
};
